/**
 * UseBindEditor — smart bind mapping editor for ::use blocks.
 *
 * Scans the source definition's config to discover $ref{} dependencies,
 * then provides autocomplete for both bind keys (source refs) and
 * bind values (available definitions in the document).
 */

import type {
    ImportCachedSymbol,
    PluginDefinition,
    UseBinding,
} from "@magic-md/core";
import { OutputSerializer } from "@magic-md/core";
import { REF_ID_RE } from "@magic-md/core/patterns";
import type { LocaleParams } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import { ChevronDown, ChevronRight, Link, Plus, Trash2, Unlink } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";
import {
    resolveSerializerTargetLocator,
    type EditorSerializerTargetLocator,
} from "../lib/unit-edit-target";
import { useEditorStore } from "../store/editor-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

const outputSerializer = new OutputSerializer();
type EditorT = (key: string, params?: LocaleParams) => string;

/* ── Types ── */

interface UseBindEditorProps {
    /** The ::use binding from parsedDocument.uses */
    useBinding: UseBinding;
    /** All available definitions in the document (from PaneContext) */
    definitions: Map<string, PluginDefinition>;
    /** The source definition's config (to extract $ref{} dependencies) */
    sourceConfig: Record<string, unknown>;
    /** Import cached symbols (to find dependencies of imported symbols) */
    importSymbols?: ImportCachedSymbol[];
    /** Exact source occurrence when opened from a CodeMirror unit widget. */
    locator?: EditorSerializerTargetLocator;
}

interface BindEntry {
    key: string;
    value: string;
}

/* ── Helpers ── */

/**
 * Extract all definition IDs referenced via `$ref{someId.xxx}` in a config object.
 * Returns unique definition IDs (not including the current use's own source).
 */
function extractConfigRefs(config: unknown): string[] {
    const refs = new Set<string>();
    const json = JSON.stringify(config);
    const pattern = new RegExp(REF_ID_RE.source, "g");
    for (const match of json.matchAll(pattern)) {
        refs.add(match[1]);
    }
    return Array.from(refs);
}

/**
 * Given an imported symbol's qualifiedId (e.g. "du/hn-story-details"),
 * extract the original ID without namespace (e.g. "hn-story-details").
 */
function stripNamespace(qualifiedId: string): string {
    const slashIdx = qualifiedId.indexOf("/");
    return slashIdx >= 0 ? qualifiedId.slice(slashIdx + 1) : qualifiedId;
}

/**
 * Given the source symbol's qualifiedId (e.g. "du/dynamicUI") and its config,
 * find all $ref{} dependencies — both the raw ref IDs from config and
 * the corresponding qualified IDs from import symbols.
 */
function discoverBindableRefs(
    sourceQualifiedId: string,
    sourceConfig: Record<string, unknown>,
    t: EditorT,
    importSymbols?: ImportCachedSymbol[],
): Array<{ key: string; label: string; description?: string }> {
    const refIds = extractConfigRefs(sourceConfig);

    // If this is an imported symbol, refs in its config reference original IDs
    // (e.g., "hn-story-details" not "du/hn-story-details")

    const results: Array<{ key: string; label: string; description?: string }> =
        [];
    const seen = new Set<string>();

    for (const refId of refIds) {
        // Skip self-references and input refs
        if (refId === sourceQualifiedId || refId.startsWith("input/")) continue;

        // The ref might be local to the imported module (e.g., "hn-story-details")
        // or already qualified (e.g., "du/hn-story-details")
        if (!seen.has(refId)) {
            seen.add(refId);

            // Find matching import symbol for description
            let description: string | undefined;
            if (importSymbols) {
                const sym = importSymbols.find(
                    (s) =>
                        s.originalId === refId ||
                        s.qualifiedId === refId ||
                        stripNamespace(s.qualifiedId) === refId,
                );
                if (sym) {
                    description = sym.pluginName
                        ? t("useBind.pluginLabel", { name: sym.pluginName })
                        : sym.kind;
                }
            }

            results.push({
                key: refId,
                label: refId,
                description,
            });
        }
    }

    return results;
}

/* ── FilterableDropdown (simple inline combobox with portal) ── */

export function FilterableDropdown({
    value,
    onChange,
    options,
    placeholder,
    className,
}: {
    value: string;
    onChange: (value: string) => void;
    options: Array<{
        value: string;
        label: string;
        group?: string;
        description?: string;
    }>;
    placeholder?: string;
    className?: string;
}) {
    const t = useT("editor");
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const filtered = useMemo(() => {
        if (!filter) return options;
        const lowerFilter = filter.toLowerCase();
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(lowerFilter) ||
                o.value.toLowerCase().includes(lowerFilter) ||
                (o.description?.toLowerCase().includes(lowerFilter) ?? false),
        );
    }, [options, filter]);

    // Group by group field
    const grouped = useMemo(() => {
        const groups = new Map<string, typeof filtered>();
        for (const opt of filtered) {
            const g = opt.group ?? "";
            if (!groups.has(g)) groups.set(g, []);
            groups.get(g)!.push(opt);
        }
        return groups;
    }, [filtered]);

    // Position the portal dropdown below the input
    useEffect(() => {
        if (!isOpen || !inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownStyle({
            position: "fixed",
            top: rect.bottom + 2,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    }, [isOpen]);

    // Close on outside click — check both container and portal dropdown
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (containerRef.current?.contains(target)) return;
            if (dropdownRef.current?.contains(target)) return;
            setIsOpen(false);
            setFilter("");
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen]);

    const displayValue = value
        ? (options.find((o) => o.value === value)?.label ?? value)
        : "";

    const dropdownContent = isOpen && filtered.length > 0 ? (
        <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="max-h-48 overflow-auto rounded-md border border-border bg-popover shadow-lg"
            data-comboselect-portal
        >
            {Array.from(grouped.entries()).map(([groupName, items]) => (
                <div key={groupName}>
                    {groupName && (
                        <div className="px-2 py-1 text-[9px] font-semibold text-muted-foreground/60 uppercase tracking-wider bg-muted/30 sticky top-0">
                            {groupName}
                        </div>
                    )}
                    {items.map((opt) => (
                        <button
                            type="button"
                            key={opt.value}
                            className={cn(
                                "flex flex-col w-full px-2 py-1.5 text-left hover:bg-accent transition-colors",
                                opt.value === value && "bg-accent/50",
                            )}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(opt.value);
                                setIsOpen(false);
                                setFilter("");
                            }}
                        >
                            <span className="text-[11px] font-mono text-foreground truncate">
                                {opt.label}
                            </span>
                            {opt.description && (
                                <span className="text-[9px] text-muted-foreground/70 truncate">
                                    {opt.description}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    ) : null;

    return (
        <div ref={containerRef} className={cn("relative", className)}>
            <input
                ref={inputRef}
                type="text"
                className="w-full h-7 rounded border border-border bg-background px-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                value={isOpen ? filter : displayValue}
                placeholder={placeholder ?? t("useBind.selectPlaceholder")}
                onChange={(e) => {
                    setFilter(e.target.value);
                    if (!isOpen) setIsOpen(true);
                }}
                onFocus={() => {
                    setIsOpen(true);
                    setFilter("");
                }}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        setIsOpen(false);
                        setFilter("");
                        inputRef.current?.blur();
                    } else if (e.key === "Enter" && filtered.length > 0) {
                        onChange(filtered[0].value);
                        setIsOpen(false);
                        setFilter("");
                        inputRef.current?.blur();
                    }
                }}
            />
            {dropdownContent && createPortal(dropdownContent, document.body)}
        </div>
    );
}

/* ── UseBindEditor ── */

export function UseBindEditor({
    useBinding,
    definitions,
    sourceConfig,
    importSymbols,
    locator,
}: UseBindEditorProps) {
    const t = useT("editor");
    // Current bind from parsed document
    const persistedBind = useBinding.bind ?? {};

    // Collapsible state — default expanded if there are existing bindings
    const [expanded, setExpanded] = useState(
        () => Object.keys(persistedBind).length > 0,
    );

    // Local draft state
    const [entries, setEntries] = useState<BindEntry[]>(() =>
        Object.entries(persistedBind).map(([key, value]) => ({ key, value })),
    );

    const targetKey = [
        useBinding.id,
        locator?.startLine ?? "",
        locator?.endLine ?? "",
        locator?.occurrence ?? "",
        useBinding.range.start,
        useBinding.range.end,
    ].join(":");
    // Track the concrete use occurrence to reset state on same-ID switches.
    const lastUseTargetKey = useRef(targetKey);
    useEffect(() => {
        if (targetKey !== lastUseTargetKey.current) {
            lastUseTargetKey.current = targetKey;
            setEntries(
                Object.entries(useBinding.bind ?? {}).map(([key, value]) => ({
                    key,
                    value,
                })),
            );
        }
    }, [targetKey, useBinding.bind]);

    // Sync from document when not dirty
    const persistedJson = JSON.stringify(persistedBind);
    const entriesJson = useMemo(() => {
        const obj: Record<string, string> = {};
        for (const e of entries) {
            if (e.key) obj[e.key] = e.value;
        }
        return JSON.stringify(obj);
    }, [entries]);
    const isDirty = entriesJson !== persistedJson;

    // Discover bindable refs from source config
    const bindableRefs = useMemo(
        () => discoverBindableRefs(useBinding.source, sourceConfig, t, importSymbols),
        [useBinding.source, sourceConfig, t, importSymbols],
    );

    // Build options for bind key dropdown
    const keyOptions = useMemo(() => {
        const usedKeys = new Set(entries.map((e) => e.key));
        return bindableRefs
            .filter((r) => !usedKeys.has(r.key))
            .map((r) => ({
                value: r.key,
                label: r.label,
                description: r.description,
                group: t("useBind.sourceDependencies"),
            }));
    }, [bindableRefs, entries, t]);

    // Build options for bind value dropdown (all available definitions)
    const valueOptions = useMemo(() => {
        const opts: Array<{
            value: string;
            label: string;
            group: string;
            description?: string;
        }> = [];
        for (const [id, def] of definitions) {
            // Skip the use definition itself
            if (id === useBinding.id) continue;
            opts.push({
                value: id,
                label: id,
                group: id.includes("/")
                    ? t("useBind.importedDefinitions")
                    : t("useBind.localDefinitions"),
                description: def.pluginName,
            });
        }
        return opts;
    }, [definitions, useBinding.id, t]);

    // Write bind back to document
    const applyBind = useCallback(() => {
        const bind: Record<string, string> = {};
        for (const e of entries) {
            if (e.key && e.value) bind[e.key] = e.value;
        }

        const editorState = useEditorStore.getState();
        const activeTab = editorState.getActiveTab();
        if (!activeTab) return;

        // Read existing patch/with from the parsed use binding
        const patch = useBinding.patch ?? {};
        const withParams = useBinding.with ?? {};
        const targetLocator = resolveSerializerTargetLocator({
            content: activeTab.content,
            unitKind: "use",
            unitId: useBinding.id,
            locator,
            sourceRange: useBinding.range,
        });
        if (locator?.fingerprint && !targetLocator) return;

        const updated = outputSerializer.updateUseConfig(
            activeTab.content,
            useBinding.id,
            patch,
            { bind, with: withParams },
            targetLocator,
        );

        if (updated !== activeTab.content) {
            editorState.setContentImmediate(updated, activeTab.id);
        }
    }, [entries, useBinding.id, useBinding.patch, useBinding.with, useBinding.range, locator]);

    // Handlers
    const addEntry = useCallback(() => {
        setEntries((prev) => [...prev, { key: "", value: "" }]);
    }, []);

    const removeEntry = useCallback((index: number) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const updateEntryKey = useCallback((index: number, key: string) => {
        setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, key } : e)));
    }, []);

    const updateEntryValue = useCallback((index: number, value: string) => {
        setEntries((prev) =>
            prev.map((e, i) => (i === index ? { ...e, value } : e)),
        );
    }, []);

    // Auto-apply (add an entry with pre-selected key)
    const addEntryWithKey = useCallback((key: string) => {
        setEntries((prev) => [...prev, { key, value: "" }]);
    }, []);

    const hasUnboundRefs = keyOptions.length > 0;

    return (
        <div className="rounded-lg border border-border/30 overflow-hidden">
            {/* Card header */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 transition-colors">
                <button
                    type="button"
                    className="flex items-center gap-1.5 flex-1 min-w-0"
                    onClick={() => setExpanded((v) => !v)}
                >
                    <span className="text-muted-foreground/60"><Link size={12} /></span>
                    <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider flex-1 text-left">
                        {t("useBind.dependencyBinding")}
                    </span>
                    {!expanded && entries.length > 0 && (
                        <span className="text-[9px] font-medium text-muted-foreground/50 tabular-nums">
                            {entries.length}
                        </span>
                    )}
                    {!expanded && hasUnboundRefs && (
                        <span
                            className="text-[9px] font-medium theme-accent-text"
                            style={getThemeAccentStyle("warning")}
                        >
                            {t("useBind.bindableCount", { count: keyOptions.length })}
                        </span>
                    )}
                    {expanded ? (
                        <ChevronDown size={11} className="text-muted-foreground/50 shrink-0" />
                    ) : (
                        <ChevronRight size={11} className="text-muted-foreground/50 shrink-0" />
                    )}
                </button>
                {isDirty && (
                    <button
                        type="button"
                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 theme-accent-icon-button"
                        style={getThemeAccentStyle("success")}
                        onClick={applyBind}
                    >
                        {t("common.save")}
                    </button>
                )}
            </div>

            {/* Card body */}
            {expanded && (
                <div className="border-t border-border/20 px-2.5 py-2 space-y-2">
                    {/* Existing entries */}
                    {entries.length > 0 && (
                        <div className="space-y-1.5">
                            {entries.map((entry, index) => (
                                <div key={index} className="flex items-center gap-1.5">
                                    <FilterableDropdown
                                        value={entry.key}
                                        onChange={(key) => updateEntryKey(index, key)}
                                        options={[
                                            ...(entry.key
                                                ? [
                                                    {
                                                        value: entry.key,
                                                        label: entry.key,
                                                        group: t("common.current"),
                                                        description: bindableRefs.find(
                                                            (r) => r.key === entry.key,
                                                        )?.description,
                                                    },
                                                ]
                                                : []),
                                            ...keyOptions,
                                        ]}
                                        placeholder={t("useBind.sourceRefPlaceholder")}
                                        className="flex-1 min-w-0"
                                    />
                                    <span className="text-[10px] text-muted-foreground/50 shrink-0">→</span>
                                    <FilterableDropdown
                                        value={entry.value}
                                        onChange={(value) => updateEntryValue(index, value)}
                                        options={valueOptions}
                                        placeholder={t("useBind.targetDefinitionPlaceholder")}
                                        className="flex-1 min-w-0"
                                    />
                                    <button
                                        type="button"
                                        className="p-1 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                                        onClick={() => removeEntry(index)}
                                    >
                                        <Trash2 size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add new entry + quick-add */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                            type="button"
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border/50 transition-colors"
                            onClick={addEntry}
                        >
                            <Plus size={10} />
                            {t("useBind.addBinding")}
                        </button>
                        {hasUnboundRefs && keyOptions.slice(0, 5).map((opt) => (
                            <button
                                type="button"
                                key={opt.value}
                                className="px-1.5 py-0.5 rounded text-[9px] font-mono text-primary/60 bg-primary/5 hover:bg-primary/10 border border-primary/10 transition-colors"
                                onClick={() => addEntryWithKey(opt.value)}
                                title={opt.description}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* Empty state */}
                    {entries.length === 0 && !hasUnboundRefs && (
                        <div className="flex items-center gap-2 py-1 text-[10px] text-muted-foreground/50">
                            <Unlink size={11} />
                            <span>{t("useBind.noExternalRefs")}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
